/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import Print, { PrintData } from "../../../../print-configuration/PrintConfigration";
import LoadingScreen from "../../../../shared/components/LoadingScreen";
import { useUser } from "../../components/UserContext";
import useCategory from "../../hooks/useCategory";
import useUOM from "src/shared/hooks/useUOM";
import SubscriptionDialog from "src/shared/modules/admin/auth/components/SubscriptionDialog";
import useProductsIndexDB from "../../hooks/useProductIndexDB";
import ProductDisplay from "src/business-types/grocery/layouts/Home/ProductDisplay";
import NotesProducts, { ProductRowV2 } from "src/business-types/grocery/layouts/Home/NotesProducts";
import SidePanel from "src/business-types/grocery/layouts/Home/side-panel/SidePanel";
import Footer from "src/business-types/grocery/layouts/Home/Footer";
import CtrlFOpenPopup from "src/business-types/grocery/layouts/Home/search-model/SearchProduct";
import { getLedgerByCustomer, getOrder, postOrder } from "src/shared/api";
import { CustomerData, Order } from "src/shared/services/interfacev2";
import { formatDate, formatTime } from "src/business-types/utils/dateTimeUtils";
import { processCardPayment } from "src/paymentGatway/useCardPayment";

type OrderedItem = { productId: string; quantity: number };

const GroceryHome: React.FC = () => {
  const { user, userConfigration } = useUser();
  const { product, loadingPro, productSync } = useProductsIndexDB();
  const { category, loading } = useCategory();
  const { uom } = useUOM();
  const [orderProduct, setOrderProduct] = useState<OrderedItem[]>([]);
  const [selectProductList, setSelectProductList] = useState<ProductRowV2[]>([]);
  const [lastProduct, setLastSelectedProduct] = useState<string>("");
  const [viewProduct, setViewProduct] = useState<boolean>(true);
  const [discount, setDiscount] = useState({ discountedAmount: "", discountValue: "", mode: "" });
  const [customerDetail, setCustomerDetail] = useState<CustomerData>();
  const [receivedAmount, setReceivedAmount] = useState<number>(0);
  const [paymentMethod, setPaymentMethod] = useState<string>("");
  const [clearData, setClearData] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [lastOrderData, setLastOrderData] = useState<Order>();
  const [osb, setOSB] = useState(0);
  const [openPrint, setOpenPrint] = useState(false);
  const [orderStatus, setOrderStatus] = useState<string>("Delivered");
  const [resvedAmount, setResvedAmount] = useState<number>(0);
  const { subscriptionInfo } = useUser();
  const [showDialog, setShowDialog] = useState(false);
  const [message, setMessage] = useState<React.ReactNode>("");

  /** 📦 Add Product to Order */
  const sendToOrder = useCallback((productId: string) => {
    setOrderProduct((prev) => {
      const existing = prev.find((p) => p.productId === productId);
      if (productId === "other") {
        if (existing) {
          return prev.map((p) =>
            p.productId === "other" ? { ...p, quantity: p.quantity + 1 } : p
          );
        }
        return [
          ...prev,
          {
            productId: "other",
            productRefId: "other",
            productName: "Other",
            arabicName: "آحرون",
            qty: "1",
            price: "0",
            amount: "0",
            uom: "",
            typesUOM: [],
            stock: 0,
            notes: [],
            visibleNotes: false,
            quantity: 1,
            unitConfiguration: { baseUnitType: "", stock: 0, uom: [] },
            barcodeId: "",
          },
        ];
      }
      return existing
        ? prev.map((p) =>
          p.productId === productId ? { ...p, quantity: p.quantity + 1 } : p
        )
        : [...prev, { productId, quantity: 1 }];
    });
    setLastSelectedProduct(productId);
  }, []);
  /** 💰 Confirm Order Discount */
  const orderConfirm = useCallback((discountedAmount: number, discountValue: number, mode: string) => {
    setDiscount({
      discountedAmount: String(discountedAmount),
      discountValue: String(discountValue),
      mode,
    });
  }, []);

  /** 🔄 Reset order when clear */
  useEffect(() => {
    setOrderProduct([]);
    setLastSelectedProduct("");
  }, [clearData]);

  useEffect(() => {
    if (!subscriptionInfo) return;

    const alreadyShown = sessionStorage.getItem("subscription_dialog_shown");
    if (alreadyShown === "true") return;

    // GRACE PERIOD CASE
    if (subscriptionInfo.status === "grace_period") {
      const days = subscriptionInfo.daysRemaining;

      const formattedMessage = (
        <>
          This is a reminder that your current subscription will expire in <strong>{days} day(s)</strong>. Please renew now to avoid service disruption.
        </>
      );

      setMessage(formattedMessage);
      setShowDialog(true);
      sessionStorage.setItem("subscription_dialog_shown", "true");
      return;
    }

    // WARNING CASE
    if (subscriptionInfo.warning) {
      setMessage(subscriptionInfo.warning);
      setShowDialog(true);
      sessionStorage.setItem("subscription_dialog_shown", "true");
      return;
    }
  }, [subscriptionInfo]);

  /** 🧮 Calculate totals */
  const totalPrice = selectProductList.reduce((sum, item) => {
    const qty = Number(item.qty) || 0;
    const price = Number(item.price) || 0;

    const notesTotal = item.notes?.reduce((acc, note) => acc + (Number(note.amount) || 0), 0) || 0;

    return sum + qty * price + notesTotal;
  }, 0);
  const finalAmount = useMemo(() => totalPrice - Number(discount.discountedAmount || 0), [totalPrice, discount]);

  const isUnknownCustomer = customerDetail?.firstName === "XXXX";

  const totalPayable = isUnknownCustomer ? finalAmount : finalAmount + Number(customerDetail?.totalOutstandingBalance || 0);

  /** ✅ Product payload */
  const Products = useMemo(
    () =>
      selectProductList
        .filter((i) => i.productId && i.productName && i.qty && i.amount)
        .map((i) => ({
          productName: i.productName,
          arabicword: i.productName === "Other" ? "حرون" : i.arabicName || "",
          unitPrice: Number(i.price),
          quantity: Number(i.qty),
          itemTotal: (Number(i.qty) * Number(i.price)) + (i.notes ? i.notes.reduce((acc, note) => acc + (Number(note.amount) || 0), 0) : 0),
          productId: i.productId,
          addOns: i.notes.map((note) => ({
            text: note.text,
            amount: note.amount,
          })),
          barcodeId: i.barcodeId || "",
          itsCustomProduct: i.productName === "Other" ? "true" : "false",
          uom: uom.find((u) => u.id === i.uom)?.uomName || "",
          productRefId: i.productRefId,
        })),

    [selectProductList],
  );

  const remainingPrice = () => {
    const remaining = Math.max(0, finalAmount - receivedAmount);
    return remaining.toFixed(2);
  };

  const roundToTwo = (value: number) => {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  };

  const paid = Number(receivedAmount || 0);
  const payable = Number(totalPayable || 0);

  // Calculate applied amount and return amount based on payment vs payable
  let appliedAmount = 0;
  let changeAmount = 0;

  if (paid >= payable) {
    // Full payment or overpayment
    appliedAmount = payable; // Only apply what's due
    changeAmount = roundToTwo(paid - payable); // Return the excess as change
  } else {
    // Partial payment
    appliedAmount = paid;
    changeAmount = 0; // No change for partial payment
  }

  /** 🧾 Confirm Order */
  const confirm = useCallback(async () => {
    setIsLoading(true);
    const now = new Date();
    const localDate = now.getFullYear() + "-" + String(now.getMonth() + 1).padStart(2, "0") + "-" + String(now.getDate()).padStart(2, "0");
    const payamount = finalAmount < receivedAmount ? finalAmount : receivedAmount

    const Orderdate = localDate;
    const Ordertime = now.toTimeString().split(" ")[0];
    const data = {
      customerId: customerDetail?._id.$oid,
      products: Products,
      orderStatus: orderStatus === "Order Taken" ? "Confirm" : orderStatus,
      orderDate: Orderdate,
      orderTime: Ordertime,
      orderAmount: totalPrice,
      discount: Number(discount.discountedAmount),
      remainingAmount: Number(remainingPrice()),
      finalAmount: finalAmount,
      outstandingBalance: Number(customerDetail?.totalOutstandingBalance ?? 0) ?? 0,
      balanceReturned: changeAmount,
      payments: {
        paymentMethod: paymentMethod,
        receivedAmount: appliedAmount,
        amountDue: Number(remainingPrice()),
        changeReturned: changeAmount,
        amountGiven: receivedAmount
      },
      paymentHistory: [
        {
          paymentMethod: paymentMethod,
          receivedAmount: payamount,
        },
      ],
    };

    if (paymentMethod === "Card" && Boolean(userConfigration?.cardPayment) === true) {
      setIsLoading(false);
      await processCardPayment({
        amount: payamount,
        description: `Order Payment for ${customerDetail?.phones?.[0]?.phone || "Unknown Customer"}`,
        onSuccess: async (paymentDetails) => {
          const paysucess = {
            ...data,
            paymentDetails,
          };

          postOrderData(paysucess);
        },
      });
      return

    }
    postOrderData(data);

  }, [Products, customerDetail, discount, paymentMethod, receivedAmount, totalPrice, finalAmount, orderStatus]);

  const postOrderData = (data: any) => {
    postOrder(data)
      .then((res) => {
        setClearData(res.data.Id);
        setReceivedAmount(0);
        getOrder(res.data.Id)
          .then((res) => {
            setLastOrderData(res.data.result);
            getLedgerByCustomer(customerDetail?._id.$oid || "")
              .then((res) => {
                setOSB(res.data.ledger.totalOutstandingBalance);
                setOpenPrint(true);
              })
              .catch(console.error);
          })
          .catch(console.error);
      })
      .catch(console.error)
      .finally(() => setIsLoading(false));
  }

  /** 🖨️ Print Data */
  const PrintData: PrintData = useMemo(() => {
    const order = lastOrderData?.orders?.[0];
    const orderDetails = lastOrderData?.orderDetails?.[0];

    // ✅ Paid amount from paymentHistory
    const paidAmount =
      lastOrderData?.paymentHistory?.reduce(
        (sum, p) => sum + Number(p.receivedAmount || 0),
        0
      ) || 0;

    // ✅ Total from products
    const totalAmount =
      orderDetails?.products?.reduce((sum, p) => {
        return sum + Number(p.unitPrice) * Number(p.quantity);
      }, 0) || 0;

    const balance = Number(order?.finalAmount || 0) - paidAmount;

    // ✅ Customer Name
    const name = [
      lastOrderData?.customerDetails?.firstName,
      lastOrderData?.customerDetails?.middleName,
      lastOrderData?.customerDetails?.lastName
    ]
      .filter(Boolean)
      .join(" ");

    // ✅ Phone (your interface expects string, not array)
    const phoneNumber =
      lastOrderData?.customerDetails?.phones?.[0]?.phone || "";

    const orderDateTime = order?.createdOn.$date;

    return {
      CustomerName: name,
      CustomerPhon: phoneNumber,
      OrderId: lastOrderData?.orderId || "",
      OrderStatus: order?.status || "",
      discount: Number(order?.discount || 0),
      deliveryFee: Number(order?.deliveryFee || 0),
      Date: formatDate(orderDateTime),
      Time: formatTime(orderDateTime),

      // ✅ Convert API product → Print format
      Products:
        orderDetails?.products?.map((p) => ({
          productName: p.name,
          arabicword: p.arabicName,
          amount: Number(p.unitPrice),
          quantity: Number(p.quantity),
          totalPrice: Number(p.lineTotal),
          uom: p.uom || "",
          addOns: p.addOns || [],
          barcodeId: p.barcodeId || "",
        })) || [],

      Paid: paidAmount,
      unpaid: balance > 0 ? balance : 0,
      balanceEnabiled: true,

      returnAmount:
        lastOrderData?.paymentHistory?.[0]?.changeReturned || 0,

      balance:
        Number(order?.remainingAmount || 0),

      OSB: osb || 0,

      paymentMethod:
        lastOrderData?.paymentHistory?.[0]?.paymentMethod || "",

      total: totalAmount,
      subtotal: totalAmount - Number(order?.discount || 0),
      clientId: lastOrderData?.customerDetails?.refId?.toString() || "",
      billNo: Number(order?.billNumber || 0),

      resvedAmount: Number(lastOrderData?.paymentHistory?.[0]?.amountGiven || 0),
      balanceReturned:
        lastOrderData?.paymentHistory?.[0]?.changeReturned || 0,

      businessType: user?.businessType,
      payMentHistoryAvabile: Number(lastOrderData?.paymentHistory?.[0]?.amountGiven) > Number(lastOrderData?.orders[0].finalAmount) - Number(lastOrderData?.orders[0].discount || 0)
    };
  }, [lastOrderData, osb, user, resvedAmount]);

  return (
    <div className="flex flex-col w-full h-full p-1 gap-0.5">
      {(loading || loadingPro || isLoading) && <LoadingScreen />}
      {/* Main Content Area */}
      <div className="flex flex-col h-[90%] lg:flex-row gap-2 flex-grow overflow-auto rounded-xl">
        {/* Product Display (Left on Desktop, Full width on Mobile) */}
        {viewProduct && (
          <div className="w-full lg:w-3/5 bg-white rounded-xl shadow-md p-2 h-full">
            <ProductDisplay
              product={product}
              category={category}
              sendToOrder={sendToOrder}
              clearData={clearData}
              productLoading={setIsLoading}
              syncProduct={productSync}
            />
          </div>
        )}

        {/* Order Section */}
        <div className={`${viewProduct ? "lg:w-[60%]" : "lg:w-2/3"} w-full bg-white rounded-xl shadow-md p-2`}>
          <NotesProducts
            product={product}
            orderProduct={orderProduct}
            lastProduct={lastProduct}
            updateProduct={setSelectProductList}
            viewProduct={() => setViewProduct(!viewProduct)}
            closeProduct={!viewProduct}
            clearData={clearData}
            getUOM={uom}
          />
        </div>

        {/* Side Panel (Right Section) */}
        {!viewProduct && (
          <div className="w-full lg:w-1/3 bg-white rounded-xl shadow-md p-2">
            <SidePanel
              finalAmount={finalAmount}
              selectProductList={selectProductList}
              discount={discount}
              upDateCustomer={setCustomerDetail}
              receivedAmount={(data, method) => {
                setReceivedAmount(data);
                setPaymentMethod(method);
              }}
              clearData={clearData}
              throwStatus={(status: string) => setOrderStatus(status)}
            />
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="w-full bg-white rounded-xl shadow-md p-2 h-fit">
        <Footer
          selectProductList={selectProductList}
          orderConfirm={orderConfirm}
          receivedAmount={customerDetail}
          closeProduct={viewProduct}
          placeConfirm={confirm}
          clearData={clearData}
          closeProductTab={() => setViewProduct(false)}
          paymentMethod={paymentMethod}
          received={receivedAmount}
          setResvedAmount={setResvedAmount}
          resvedAmount={resvedAmount}
          isUnknownCustomer={customerDetail?.firstName === "XXXX"}
          hasCustomer={!!customerDetail}
        />
      </div>
      <SubscriptionDialog
        open={showDialog}
        message={message}
        onClose={() => setShowDialog(false)}
        onRenew={() => {
          setShowDialog(false);
        }}
      />

      <CtrlFOpenPopup />
      {/* Print */}
      {openPrint && <Print PrintData={PrintData} username="" openPrint={lastOrderData?.orderId} isClosed={() => setOpenPrint(false)} barcodeTrue />}
    </div>
  );
};

export default React.memo(GroceryHome);
